import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';
import { siteUrl, siteByKey } from '../lib/sites';
import BusinessDashboard from '../components/reports/BusinessDashboard';
import OwnerMetricsReport from '../components/reports/OwnerMetricsReport';
import BillsReport from '../components/reports/BillsReport';

// ── Reports — reports.silverservice.com ──
//
// Company reporting for the owners (Olaf, Alet) only: sales by rep, the
// business dashboard and owner metrics, moved off Manage (docs/site-map.md),
// and the open QuickBooks bills.
// The shared staff sign-in, and Manage's /admin/* routes (like the Purchasing
// site); the backend refuses these reports to every role but owner, so this
// shell only decides what to draw.
export default function Reports() {
  const session = useStaffSession('reports');
  const { token } = session;
  const [me, setMe] = useState(null);
  // ?tab= lets the Monday bills email land straight on Bills.
  const [tab, setTab] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get('tab');
    return TABS.some(([key]) => key === asked) ? asked : 'reps';
  });

  useEffect(() => {
    if (token) adminApi.me().then(setMe).catch(() => {});
  }, [token]);

  if (!token) return <StaffSignIn session={session} siteName="Reports" />;

  const header = (
    <div style={s.topBar}>
      <div>
        <h1 style={s.pageTitle}>Silver Service Reports</h1>
        <p style={s.sub}>How the business is doing — owners only</p>
      </div>
      <div style={s.topRight}>
        {me && <span style={s.who}>{me.name || me.email}</span>}
        <AllSitesLink style={s.btnOutline} />
        <button style={s.btnOutline} onClick={session.signOut} title="Signs this browser out of every staff site">Sign out</button>
      </div>
    </div>
  );

  if (me && me.role !== 'owner') {
    return (
      <div style={s.page}><div style={s.container}>
        {header}
        <div style={s.notice}>Reports are for Olaf and Alet. Your numbers are on the <a href={siteUrl(siteByKey('sales'))}>sales site</a>.</div>
      </div></div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.container}>
        {header}
        <div style={s.tabs}>
          {TABS.map(([key, label]) => (
            <button key={key} style={tab === key ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
        {!me ? <p style={s.sub}>Loading…</p> : (
          <>
            {tab === 'reps' && <SalesByRep />}
            {tab === 'business' && <BusinessDashboard />}
            {tab === 'metrics' && <OwnerMetricsReport />}
            {tab === 'bills' && <BillsReport />}
          </>
        )}
      </div>
    </div>
  );
}

const TABS = [['reps', 'Sales by rep'], ['business', 'Business'], ['metrics', 'Owner metrics'], ['bills', 'Bills']];

const money = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');

function Change({ now, then }) {
  if (then == null) return <span style={s.dim}>no history</span>;
  if (!then) return now > 0 ? <span style={s.dim}>new</span> : null;
  const pct = ((now - then) / then) * 100;
  return <span style={{ color: pct >= 0 ? '#1a7f37' : '#b3261e', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%</span>;
}

// Every rep's book, side by side, on the same basis as the sales site
// (bonus-sheet spend, equipment left out; GP over the last 12 full months).
function SalesByRep() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { adminApi.getSalesByRep().then(setD).catch((e) => setError(e.message)); }, []);
  if (error) return <p style={s.err}>{error}</p>;
  if (!d) return <p style={s.sub}>Loading…</p>;
  const reps = d.reps || [];
  const total = reps.reduce((t, r) => ({
    accounts: t.accounts + r.accounts, avgMonthly: t.avgMonthly + r.avgMonthly, thisQuarter: t.thisQuarter + r.thisQuarter,
    lastQuarter: t.lastQuarter + r.lastQuarter, spend12: t.spend12 + r.spend12, gp12: t.gp12 + r.gp12,
    atRisk: t.atRisk + r.status['at-risk'], dormant: t.dormant + r.status.dormant,
  }), { accounts: 0, avgMonthly: 0, thisQuarter: 0, lastQuarter: 0, spend12: 0, gp12: 0, atRisk: 0, dormant: 0 });
  const p = d.periods;
  return (
    <div>
      <p style={s.sub}>
        Each rep's customers, on the bonus-sheet basis (SOS orders, quantity × unit price, equipment left out).
        {d.metricsFetchedAt ? ` Numbers as of ${new Date(d.metricsFetchedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.` : ''}
        {d.metricsStatus === 'running' ? ' Refreshing now — reload in a minute.' : ''}
      </p>
      <div style={s.tableScroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Rep</th>
              <th style={s.thR}>Customers</th>
              <th style={s.thR}>Avg / month</th>
              <th style={s.thR}>{p ? `${p.qtd.label} so far` : 'This quarter'}</th>
              <th style={s.thR}>{p ? p.lastQ.label : 'Last quarter'}</th>
              <th style={s.thR}>GP, 12 months</th>
              <th style={s.thR}>At-risk</th>
              <th style={s.thR}>Dormant</th>
            </tr>
          </thead>
          <tbody>
            {reps.map((r) => (
              <tr key={r.owner}>
                <td style={{ ...s.td, fontWeight: 600 }}>{r.owner}</td>
                <td style={s.tdR}>{r.accounts}</td>
                <td style={s.tdR}>{money(r.avgMonthly)}</td>
                <td style={s.tdR}>{money(r.thisQuarter)}<div><Change now={r.thisQuarter} then={r.thisQuarterLy} /></div></td>
                <td style={s.tdR}>{money(r.lastQuarter)}<div><Change now={r.lastQuarter} then={r.lastQuarterLy} /></div></td>
                <td style={s.tdR}>{money(r.gp12)}<div style={s.dim}>{r.gpPct12 != null ? `${r.gpPct12}%` : ''}</div></td>
                <td style={{ ...s.tdR, color: r.status['at-risk'] ? '#b26a00' : '#999' }}>{r.status['at-risk']}</td>
                <td style={{ ...s.tdR, color: r.status.dormant ? '#b3261e' : '#999' }}>{r.status.dormant}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...s.td, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>Total</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{total.accounts}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{money(total.avgMonthly)}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{money(total.thisQuarter)}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{money(total.lastQuarter)}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{money(total.gp12)}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{total.atRisk}</td>
              <td style={{ ...s.tdR, fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>{total.dormant}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ ...s.sub, marginTop: 10 }}>Click through to any rep's accounts on the sales site (All sites → Sales, then filter by owner).</p>
    </div>
  );
}

const RED = '#A50B18';
const s = {
  page: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', justifyContent: 'center', padding: '2rem 1rem', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' },
  container: { width: '100%', maxWidth: 1100 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' },
  topRight: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  who: { color: '#666', fontSize: '0.85rem', marginRight: 4 },
  pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: '0 0 4px' },
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  dim: { color: '#999', fontSize: '0.78rem' },
  err: { color: '#c0392b', fontSize: '0.9rem' },
  notice: { background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, padding: '0.8rem 1rem', fontSize: '0.92rem' },
  btnOutline: { background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.82rem', cursor: 'pointer' },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 0, marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' },
  tab: { padding: '0.6rem 0.95rem', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { color: RED, borderBottom: `2px solid ${RED}`, fontWeight: 600 },
  tableScroll: { width: '100%', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10 },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.76rem', textTransform: 'uppercase', letterSpacing: 1 },
  thR: { textAlign: 'right', padding: '10px 14px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.76rem', textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 14px', borderBottom: '1px solid #f3f3f3', fontSize: '.92rem', color: '#333' },
  tdR: { textAlign: 'right', padding: '10px 14px', borderBottom: '1px solid #f3f3f3', fontSize: '.92rem', color: '#333', fontVariantNumeric: 'tabular-nums' },
};
